import { isDateBeforeToday } from '../helpers';
import { Listing } from 'graphql.generated';
import { ListingComputedFields } from 'search-map-view/types/SearchMapView.types';
import { ResultsItem } from 'search/SearchContext';

type PriceDropProps =
    | {
          percentageChangeLabel: string;
          lastPriceChangeDate: string;
      }
    | undefined;

export type FormattedListing = Listing & {
    flag?: ReturnType<typeof getFlagLabel>;
    underOffer: ReturnType<typeof isUnderOffer>;
    isImmediatelyAvailable: ReturnType<typeof getIsImmediatelyAvailable>;
    availableFromText: ReturnType<typeof getAvailableFromText>;
    availableFromLabel: ReturnType<typeof getAvailableFromLabelText>;
    priceDropValue: PriceDropProps;
    presentationType?: string;
};

type ListingFlag = Listing['flag'];

const availableFromLabel = 'Available from';

const getPriceDropProps = (priceDrop: Listing['priceDrop']) => {
    if (!priceDrop) {
        return undefined;
    }

    const { lastPriceChangeDate, percentageChangeLabel } = priceDrop;

    return percentageChangeLabel
        ? {
              percentageChangeLabel,
              lastPriceChangeDate: `Last reduced: ${lastPriceChangeDate}`
          }
        : undefined;
};

const getFlagLabel = (flag: ListingFlag) =>
    flag !== 'Under offer' ? flag : null;

const isUnderOffer = (flag: ListingFlag) => flag === 'Under offer';

const getIsImmediatelyAvailable = (availableFrom: Listing['availableFrom']) =>
    availableFrom && isDateBeforeToday(availableFrom);

const getAvailableFromText = (availableFrom: Listing['availableFrom']) => {
    return getIsImmediatelyAvailable(availableFrom)
        ? 'immediately'
        : availableFrom;
};

const getAvailableFromLabelText = (
    availableFrom: Listing['availableFrom'],
    translatedAvailableFromLabel: string
) => {
    return getIsImmediatelyAvailable(availableFrom)
        ? 'Available'
        : translatedAvailableFromLabel;
};

export const formatListings = (
    listings: Array<Listing>
): Array<FormattedListing & ListingComputedFields> => {
    const formattedListings = listings.map((listing) => {
        const { flag, availableFrom, priceDrop } = listing;

        return {
            ...listing,
            flag: getFlagLabel(flag),
            underOffer: isUnderOffer(flag),
            availableFrom: getAvailableFromText(availableFrom),
            availableFromLabel: getAvailableFromLabelText(
                availableFrom,
                availableFromLabel
            ),
            priceDrop: getPriceDropProps(priceDrop),
            isFavourite: false
        };
    });

    return formattedListings as Array<FormattedListing & ListingComputedFields>;
};

export const formatListingsV2 = (
    results: Array<ResultsItem>
): Array<FormattedListing & ListingComputedFields> => {
    const formattedListings = results.map((feedListingItem) => {
        const { listing, presentationType } = feedListingItem.data;
        const { flag, availableFrom, priceDrop } = listing;

        return {
            ...listing,
            flag: getFlagLabel(flag),
            underOffer: isUnderOffer(flag),
            availableFrom: getAvailableFromText(availableFrom),
            availableFromLabel: getAvailableFromLabelText(
                availableFrom,
                availableFromLabel
            ),
            priceDrop: getPriceDropProps(priceDrop),
            isFavourite: false,
            presentationType
        };
    });

    return formattedListings as Array<FormattedListing & ListingComputedFields>;
};
